/**
 * DocuSign envelope email subject/blurb defaults — browser/CRA uses this ESM file.
 * Keep in sync with docusignEmailDefaults.cjs (Node/server).
 */

import { getPlatformRegistry } from './platformRegistry';

const DOC_LABEL_RULES = [
  { match: /statement[-_\s]?of[-_\s]?advice|(^|[-_\s.])soa([-_\s.]|$)/i, label: 'Statement of Advice', order: 1 },
  { match: /fact[-_\s]?find/i, label: 'Fact Find', order: 2 },
  { match: /risk[-_\s]?(profile|tolerance)/i, label: 'Risk Tolerance Questionnaire', order: 3 },
  { match: /hub24/i, label: 'HUB24 Application', order: 4 },
  { match: /client[-_\s]?service[-_\s]?agreement|(^|[-_\s.])csa([-_\s.]|$)/i, label: 'Client Service Agreement', order: 5 },
  { match: /financial[-_\s]?services[-_\s]?guide|(^|[-_\s.])fsg([-_\s.]|$)/i, label: 'Financial Services Guide And Adviser Profile ProForm', order: 6 },
  { match: /third[-_\s]?party[-_\s]?authority|(^|[-_\s.])tpa([-_\s.]|$)/i, label: 'Third Party Authority Form', order: 7 },
  { match: /aml|anti[-_\s]?authority|anti[-_\s]?money/i, label: 'AML Authority Form', order: 8 },
  { match: /quotes?/i, label: 'Quotes', order: 9 },
  { match: /(^|[-_\s.])noi([-_\s.]|$)/i, label: 'NOI', order: 10 },
  { match: /ar[-_\s]?soa/i, label: 'Annual Review Statement of Advice', order: 20 },
  { match: /ar[-_\s]?roa|record[-_\s]?of[-_\s]?advice|(^|[-_\s.])roa([-_\s.]|$)/i, label: 'Record of Advice', order: 21 },
  { match: /eo[-_\s]?letter|execution[-_\s]?only/i, label: 'Execution Only Letter', order: 22 },
];

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * "<Platform> Application" for any platform in Admin → Portfolios (Dash,
 * Netwealth, …): an application-form filename naming the platform's slug or
 * display name as a whole word. The HUB24 rule above keeps its exact label.
 */
function platformApplicationRule(name) {
  // Only application forms — a platform statement or report keeps its own name.
  if (!/applic|(^|[-_\s.])app([-_\s.]|$)|form/i.test(name)) return null;
  const registry = getPlatformRegistry();
  for (const platform of registry.platforms || []) {
    const label = String((platform && platform.label) || '').trim();
    if (!label) continue;
    const words = [platform.slug, label].filter(Boolean).map(escapeRegExp).join('|');
    const re = new RegExp(`(^|[-_\\s.])(${words})([-_\\s.]|$)`, 'i');
    if (re.test(name)) return { label: `${label} Application`, order: 4 };
  }
  return null;
}

const OLD_GENERIC_BLURB_RE = /^Please review and sign the attached documents from .+\.$/;
const SOA_GENERATED_MARKER = 'clear colour photos of your Photo Identification';

function resolveFirmName(opts = {}) {
  if (typeof opts === 'string') return opts || 'your adviser';
  return opts.firmName || opts.companyName || opts.teamName || 'your adviser';
}

/** Firm segment from pre-client-name subjects: "{Firm} — please sign…". */
export function extractFirmFromLegacySubject(subject) {
  const stored = String(subject || '').trim();
  if (!stored || !stored.includes(' — please sign')) return '';
  // New format already has "{Client} - {Firm} — please sign"
  if (/^.+ - .+ — please sign/.test(stored)) return '';
  const match = stored.match(/^(.+?) — please sign/);
  return match ? match[1].trim() : '';
}

/** Phone from a generated SOA blurb, when company_info is unavailable on the client. */
export function extractPhoneFromSoaBlurb(blurb) {
  const match = String(blurb || '').match(
    /contact our office directly on ([^\n.]+)\.?/i,
  );
  return match ? match[1].trim() : '';
}

export function firmShortName(firmName) {
  const firm = String(firmName || '').trim();
  if (!firm) return 'Ready Advice';
  return firm
    .replace(/\s+Pty\.?\s+Limited\.?\s*$/i, '')
    .replace(/\s+Pty\.?\s+Ltd\.?\s*$/i, '')
    .replace(/\s+Limited\.?\s*$/i, '')
    .replace(/\s+Ltd\.?\s*$/i, '')
    .trim() || firm;
}

function documentFileName(doc) {
  if (!doc) return '';
  if (typeof doc === 'string') return doc;
  return String(doc.name || doc.originalName || doc.filename || '').trim();
}

function humanizeUnknownDocumentName(filename, clientName) {
  let base = String(filename || '').replace(/\.[^.]+$/, '');
  base = base.replace(/^\d{4}-\d{2}-\d{2}-/, '');
  const clientSlug = String(clientName || '').trim().replace(/\s+/g, '-');
  if (clientSlug && base.toLowerCase().startsWith(`${clientSlug.toLowerCase()}-`)) {
    base = base.slice(clientSlug.length + 1);
  }
  const cleaned = base.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'Document';
  return cleaned.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function labelForDocumentName(filename, clientName) {
  const name = String(filename || '');
  for (const rule of DOC_LABEL_RULES) {
    if (rule.match.test(name)) {
      return { label: rule.label, order: rule.order };
    }
  }
  const platformRule = platformApplicationRule(name);
  if (platformRule) return platformRule;
  return {
    label: humanizeUnknownDocumentName(name, clientName),
    order: 100,
  };
}

export function buildAttachmentLabels(documents, clientName) {
  const labels = [];
  const seen = new Set();
  const ranked = (Array.isArray(documents) ? documents : [])
    .map((doc, index) => {
      const filename = documentFileName(doc);
      const { label, order } = labelForDocumentName(filename, clientName);
      return { label, order, index, filename };
    })
    .filter((entry) => entry.filename);

  ranked.sort((a, b) => (a.order - b.order) || (a.index - b.index));

  for (const entry of ranked) {
    const key = entry.label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    labels.push(entry.label);
  }
  return labels;
}

function formatAttachmentList(labels) {
  if (!labels.length) return '';
  return labels.map((label, i) => `${i + 1}. ${label}`).join('\n');
}

export function isSoaEnvelope({ envelopeName, templateType } = {}) {
  const type = String(templateType || '').trim();
  const name = String(envelopeName || '').trim().toLowerCase();
  return type === 'onboardingPack' || name === 'statement of advice';
}

/** DocuSign rejects a send whose email subject is longer than this. */
export const DOCUSIGN_EMAIL_SUBJECT_MAX_LENGTH = 100;

/**
 * A subject that fits DocuSign's limit: cut at the end (the envelope name in our
 * subjects) with "…". A long envelope name made the default subject too long
 * and DocuSign refused the send (Oct 29 2026).
 */
export function fitEmailSubject(subject) {
  const text = String(subject || '').trim();
  if (text.length <= DOCUSIGN_EMAIL_SUBJECT_MAX_LENGTH) return text;
  return `${text.slice(0, DOCUSIGN_EMAIL_SUBJECT_MAX_LENGTH - 1).trimEnd()}…`;
}

export function buildEnvelopeEmailSubject({ clientName, firmName, envelopeName } = {}) {
  const firm = firmName || 'your adviser';
  const namePart = envelopeName ? `: ${envelopeName}` : ' these documents';
  const clientPart = String(clientName || '').trim();
  if (clientPart) {
    return fitEmailSubject(`${clientPart} - ${firm} — please sign${namePart}`);
  }
  return fitEmailSubject(`${firm} — please sign${namePart}`);
}

export function buildGenericEnvelopeBlurb(firmName) {
  const firm = firmName || 'your adviser';
  return `Please review and sign the attached documents from ${firm}.`;
}

export function buildSoaEnvelopeBlurb({
  clientFirstName,
  firmName,
  phoneNumber,
  documents,
  clientName,
} = {}) {
  const first = String(clientFirstName || '').trim() || 'there';
  const firm = firmName || 'your adviser';
  const shortFirm = firmShortName(firm);
  const phone = String(phoneNumber || '').trim();
  const attachmentBlock = formatAttachmentList(buildAttachmentLabels(documents, clientName));

  const lines = [
    `Hi ${first},`,
    '',
    'Further to recent discussions regarding your Superannuation Review, please find documents within the DocuSign Link for your review and signing. Follow the links to complete the signing.',
  ];

  if (attachmentBlock) {
    lines.push('', 'Attached:', '', attachmentBlock);
  }

  lines.push(
    '',
    'Once you have signed these documents you must provide clear colour photos of your Photo Identification (Driver’s License or Passport) and your Tax File Number.',
    '',
  );

  if (phone) {
    lines.push(
      `Should you have any questions regarding the documents, please do not hesitate to contact our office directly on ${phone}.`,
    );
  } else {
    lines.push(
      'Should you have any questions regarding the documents, please do not hesitate to contact our office directly.',
    );
  }

  lines.push('', 'Kind Regards,', shortFirm);
  return lines.join('\n');
}

/**
 * Default DocuSign email subject and blurb.
 * Accepts either legacy (firmName, envelopeName) or an options object.
 */
export function buildTeamAwareEmailDefaults(contextOrFirmName, envelopeNameOrOpts) {
  const legacyFirmCall = typeof contextOrFirmName === 'string'
    || (contextOrFirmName == null && typeof envelopeNameOrOpts === 'string');

  let opts;
  if (legacyFirmCall) {
    opts = {
      firmName: typeof contextOrFirmName === 'string' ? contextOrFirmName : '',
      envelopeName: typeof envelopeNameOrOpts === 'string' ? envelopeNameOrOpts : '',
    };
  } else if (
    contextOrFirmName
    && typeof contextOrFirmName === 'object'
    && (envelopeNameOrOpts == null || typeof envelopeNameOrOpts === 'string')
    && contextOrFirmName.envelopeName == null
    && contextOrFirmName.clientName == null
    && contextOrFirmName.documents == null
    && contextOrFirmName.templateType == null
  ) {
    opts = {
      ...contextOrFirmName,
      envelopeName: typeof envelopeNameOrOpts === 'string'
        ? envelopeNameOrOpts
        : contextOrFirmName.envelopeName,
    };
  } else {
    opts = { ...(contextOrFirmName || {}) };
    if (typeof envelopeNameOrOpts === 'string' && opts.envelopeName == null) {
      opts.envelopeName = envelopeNameOrOpts;
    }
  }

  const firmName = resolveFirmName(opts);
  const envelopeName = opts.envelopeName || '';
  const subject = buildEnvelopeEmailSubject({
    clientName: opts.clientName,
    firmName,
    envelopeName,
  });

  const blurb = isSoaEnvelope(opts)
    ? buildSoaEnvelopeBlurb({
      clientFirstName: opts.clientFirstName,
      firmName,
      phoneNumber: opts.phoneNumber,
      documents: opts.documents,
      clientName: opts.clientName,
    })
    : buildGenericEnvelopeBlurb(firmName);

  return { subject, blurb };
}

/** Legacy subject format before team-aware defaults. */
export function legacyEnvelopeEmailSubject(envelopeName) {
  return envelopeName ? `Sign: ${envelopeName}` : 'Please sign these documents';
}

function normalizeEmailOptions(firmNameOrOptions) {
  if (firmNameOrOptions && typeof firmNameOrOptions === 'object') {
    return firmNameOrOptions;
  }
  return { firmName: firmNameOrOptions || '' };
}

export function isGeneratedSoaBlurb(blurb) {
  const text = String(blurb || '');
  return text.includes(SOA_GENERATED_MARKER);
}

export function isUpgradeableSubject(storedSubject, opts = {}) {
  const stored = String(storedSubject || '').trim();
  if (!stored) return true;
  if (stored.startsWith('Sign:')) return true;
  if (stored === legacyEnvelopeEmailSubject(opts.envelopeName)) return true;

  const firmName = resolveFirmName(opts);
  const desired = buildEnvelopeEmailSubject({
    clientName: opts.clientName,
    firmName,
    envelopeName: opts.envelopeName,
  });
  if (stored === desired) return false;

  const clientName = String(opts.clientName || '').trim();
  if (clientName && stored.includes(' — please sign') && !stored.startsWith(`${clientName} - `)) {
    return true;
  }

  const withoutClient = buildEnvelopeEmailSubject({
    firmName,
    envelopeName: opts.envelopeName,
  });
  if (stored === withoutClient) return true;

  return false;
}

export function isUpgradeableBlurb(storedBlurb) {
  const stored = String(storedBlurb || '').trim();
  if (!stored) return true;
  if (OLD_GENERIC_BLURB_RE.test(stored)) return true;
  return false;
}

/** True when an SOA blurb was generated by us and attachment list may need refresh. */
export function shouldRefreshSoaAttachmentList(storedBlurb, opts = {}) {
  return isSoaEnvelope(opts) && isGeneratedSoaBlurb(storedBlurb);
}

export function resolveEnvelopeEmailFields(envelope, firmNameOrOptions) {
  const baseOpts = normalizeEmailOptions(firmNameOrOptions);
  const storedSubject = (envelope?.emailSubject || '').trim();
  const storedBlurb = (envelope?.emailBlurb || '').trim();
  const firmFromSubject = extractFirmFromLegacySubject(storedSubject);
  const phoneFromBlurb = extractPhoneFromSoaBlurb(storedBlurb);
  const opts = {
    ...baseOpts,
    firmName: baseOpts.companyName || firmFromSubject || baseOpts.firmName || baseOpts.teamName || '',
    phoneNumber: baseOpts.phoneNumber || phoneFromBlurb || '',
    envelopeName: envelope?.name || baseOpts.envelopeName || '',
    templateType: envelope?.templateType || baseOpts.templateType,
    documents: baseOpts.documents != null ? baseOpts.documents : (envelope?.documents || []),
  };

  const teamDefaults = buildTeamAwareEmailDefaults(opts);
  const refreshAttachments = Boolean(baseOpts.refreshSoaAttachments)
    && shouldRefreshSoaAttachmentList(storedBlurb, opts);

  const subject = isUpgradeableSubject(storedSubject, opts)
    ? teamDefaults.subject
    : storedSubject;

  const blurb = (isUpgradeableBlurb(storedBlurb) || refreshAttachments)
    ? teamDefaults.blurb
    : storedBlurb;

  return { subject, blurb };
}
